/**
 * Run HUD (doc 11 §Run HUD, doc 17 §8).
 *
 * Drawn entirely into one UI-resolution pixel buffer: one panel per corner, 4 px
 * baseline grid, label-left / value-right stat rows, a tooltip on every icon
 * button. Stage 0 wires the layout and the widget language; the live systems
 * behind the numbers arrive with their stages.
 */

import { S } from '../content/strings.ts';
import { drawCentered, font } from '../render/font/font.ts';
import { multiplyAlpha } from '../render/primitives.ts';
import { PixelSurface } from '../render/surface.ts';
import { g, UI } from './theme.ts';
import {
  contains,
  drawBar,
  drawButton,
  drawPanel,
  drawStatRow,
  drawTitledPanel,
  drawTooltip,
  type Rect,
  type TooltipContent,
  type WidgetState,
} from './widgets.ts';

export interface HotbarItem {
  label: string;
  hotkey: string;
  tooltip: TooltipContent;
  affordable: boolean;
  /** Meta-gated (doc 08): drawn struck through, not merely unaffordable. */
  locked?: boolean;
  /** The entity itself, cropped to button size — the label is the fallback. */
  icon?: PixelSurface;
}

export interface InspectorLine {
  label: string;
  value: string;
  tone?: 'ink' | 'good' | 'warning' | 'critical';
}

/** One upgrade path row (doc 05: 3×3, pick-2-cap-1) with its buy affordance. */
export interface PathRow {
  key: 'a' | 'b' | 'c';
  name: string;
  tier: number;
  /** Cost of the next tier in alloy, or null when capped/blocked. */
  nextCost: number | null;
  buyable: boolean;
  /** Shown instead of the cost when the path is blocked (PICK 2, CAP 1...). */
  blocked?: string;
}

export interface AbilityButton {
  label: string;
  hotkey: string;
  cost: string;
  /** 0 = ready; otherwise the fraction of cooldown remaining. */
  cooldown: number;
  affordable: boolean;
  /** Aiming mode active — the button renders selected. */
  aiming: boolean;
  /** Not bought yet (doc 08): shown struck through, like a locked build slot. */
  locked?: boolean;
  tooltip: TooltipContent;
}

export interface Banner {
  title: string;
  sub: string;
  /** 0..1, already quantized to 5 steps by the caller (doc 17 §6). */
  alpha: number;
}

export interface HudState {
  resources: Array<{ label: string; value: number; rate?: number; alarm?: boolean }>;
  power: { demand: number; supply: number };
  capacitor: number;
  fights: { cleared: number; total: number };
  hotbar: HotbarItem[];
  hotbarHover: number;
  hotbarSelected: number;
  inspector: {
    title: string;
    subtitle: string;
    lines: InspectorLine[];
    paths?: PathRow[];
  } | null;
  abilities: AbilityButton[];
  abilityHover: number;
  boss: { name: string; hp: number; maxHp: number } | null;
  banner: Banner | null;
  toast: { text: string; slide: number } | null;
  /** Doc 17 §5: is the ×2 pip lit? */
  fast: boolean;
  debug: string;
}

export interface HudLayout {
  hotbar: Rect[];
  topBar: Rect;
  /** Clickable upgrade rows, in the same order as inspector.paths. */
  upgradeRows: Rect[];
  /** One slot per ability, left of the build hotbar (doc 11). */
  abilities: Rect[];
}

const TOP_H = 28;
const SPEED_W = 26;
const HOTBAR_BTN = 34;

export function hudLayout(w: number, h: number, count: number): HudLayout {
  // under the supported window size (doc 17 §1) the bar tightens its gaps
  // rather than walking off the screen edge
  const wide = count * HOTBAR_BTN + (count - 1) * g(1) + HOTBAR_BTN * 2;
  const gap = wide > w ? 2 : g(1);
  const totalW = count * HOTBAR_BTN + (count - 1) * gap;
  const x0 = Math.max(2, (w - totalW) >> 1);
  const y = h - HOTBAR_BTN - g(3);
  return {
    topBar: { x: 0, y: 0, w, h: TOP_H },
    hotbar: Array.from({ length: count }, (_, i) => ({
      x: x0 + i * (HOTBAR_BTN + gap),
      y,
      w: HOTBAR_BTN,
      h: HOTBAR_BTN,
    })),
    upgradeRows: [],
    abilities: Array.from({ length: 4 }, (_, i) => ({
      x: Math.max(2, x0 - (4 - i) * (HOTBAR_BTN + gap) - g(2)),
      y,
      w: HOTBAR_BTN,
      h: HOTBAR_BTN,
    })),
  };
}

export function hotbarHit(layout: HudLayout, x: number, y: number): number {
  return layout.hotbar.findIndex((r) => contains(r, x, y));
}

export function upgradeHit(layout: HudLayout, x: number, y: number): number {
  return layout.upgradeRows.findIndex((r) => contains(r, x, y));
}

/** Which ability slot was clicked, or -1. */
export function abilityHit(layout: HudLayout, x: number, y: number): number {
  return layout.abilities.findIndex((r) => contains(r, x, y));
}

export function drawHud(s: PixelSurface, state: HudState): HudLayout {
  s.clear();
  const layout = hudLayout(s.width, s.height, state.hotbar.length);
  drawTopBar(s, layout.topBar, state);
  drawBossBar(s, state);
  drawInspector(s, state, layout);
  drawHotbar(s, layout, state);
  drawAbility(s, layout, state);
  drawToast(s, state);
  drawBanner(s, state);
  font('micro').draw(s, state.debug, g(2), s.height - g(2) - 5, UI.inkDim, { caps: true });
  return layout;
}

/** The boss's health, front and centre — an exam deserves a blackboard. */
function drawBossBar(s: PixelSurface, state: HudState): void {
  if (!state.boss) return;
  const body = font('body');
  const w = Math.max(g(50), body.measure(state.boss.name, { caps: true }) + g(8));
  const r: Rect = { x: (s.width - w) >> 1, y: TOP_H + g(2), w, h: body.capHeight + g(5) };
  drawPanel(s, r, { raised: true });
  drawCentered(s, body, state.boss.name, s.width >> 1, r.y + g(1), UI.critical, { caps: true });
  drawBar(
    s,
    { x: r.x + g(2), y: r.y + body.capHeight + g(2), w: r.w - g(4), h: 5 },
    Math.max(0, state.boss.hp / state.boss.maxHp),
    UI.critical,
  );
}

function drawAbility(s: PixelSurface, layout: HudLayout, state: HudState): void {
  const shown = Math.min(state.abilities.length, layout.abilities.length);
  layout.abilities = layout.abilities.slice(0, shown);
  state.abilities.slice(0, shown).forEach((a, i) => {
    const r = layout.abilities[i]!;
    const st: WidgetState = a.aiming
      ? 'selected'
      : a.locked || !a.affordable || a.cooldown > 0
        ? 'disabled'
        : 'default';
    drawButton(s, r, a.label, st, { hotkey: a.hotkey, face: 'micro' });
    if (a.cooldown > 0) {
      // reload sweep: a bar filling from the bottom as the cooldown runs out
      const hh = Math.round((r.h - 4) * a.cooldown);
      s.fillRect(r.x + 2, r.y + r.h - 2 - hh, r.w - 4, 1, UI.warning);
    }
    if (a.locked) s.hline(r.x + 3, r.x + r.w - 4, r.y + (r.h >> 1), UI.critical);
  });
  // doc 17 §8: an icon button without a tooltip is a bug — the price, the
  // cooldown and the joke all live there rather than crowding the bar
  const hovered = state.abilities[state.abilityHover];
  const at = layout.abilities[state.abilityHover];
  if (hovered && at) drawTooltip(s, hovered.tooltip, at, { w: s.width, h: s.height });
}

function drawTopBar(s: PixelSurface, r: Rect, state: HudState): void {
  s.fillRect(r.x, r.y, r.w, r.h, UI.panelFill);
  s.hline(r.x, r.x + r.w - 1, r.y + r.h - 1, UI.panelRimLight);

  const body = font('body');
  const micro = font('micro');
  let x = g(3);
  for (const res of state.resources) {
    micro.draw(s, res.label, x, g(1) + 1, res.alarm ? UI.critical : UI.inkDim, { caps: true });
    const value = String(res.value);
    body.draw(s, value, x, g(1) + micro.capHeight + 3, res.alarm ? UI.critical : UI.ink, { monospace: true });
    if (res.rate !== undefined && res.rate !== 0) {
      const rate = `${res.rate > 0 ? '+' : ''}${res.rate}/RD`; // next payday (doc 04)
      micro.draw(
        s,
        rate,
        x + body.measure(value, { monospace: true }) + g(1),
        g(1) + micro.capHeight + 5,
        res.rate > 0 ? UI.good : UI.critical,
        { caps: true },
      );
    }
    x += g(16);
  }

  // Power: demand over supply, brownout in the critical colour.
  const brownout = state.power.demand > state.power.supply;
  micro.draw(s, S.hud.power, x, g(1) + 1, UI.inkDim, { caps: true });
  body.draw(
    s,
    `${state.power.demand}/${state.power.supply}`,
    x,
    g(1) + micro.capHeight + 3,
    brownout ? UI.critical : UI.ink,
    { monospace: true },
  );
  x += g(18);

  micro.draw(s, S.hud.capacitor, x, g(1) + 1, UI.inkDim, { caps: true });
  drawBar(s, { x, y: g(1) + micro.capHeight + 4, w: g(14), h: 7 }, state.capacitor / 100, UI.accent);
  x += g(17);

  // doc 17 §5: F, or this. Fast-forward is available in every phase and never
  // alters a spawn script — only how quickly ticks are consumed. It lives IN
  // the bar so the top-right corner below it belongs to one column of toasts.
  const pip = speedRect(s.width);
  drawButton(s, pip, state.fast ? '2X' : '1X', state.fast ? 'selected' : 'default', { face: 'micro' });

  const fights = `FIGHTS ${state.fights.cleared}/${state.fights.total}`;
  const fw = body.measure(fights, { caps: true, monospace: false });
  body.draw(s, fights, pip.x - fw - g(2), g(2) + 1, UI.ink, { caps: true });
}

/** The ×1/×2 pip's rect — one place, so the hit test cannot drift from it. */
export function speedRect(uiW: number): Rect {
  return { x: uiW - g(3) - SPEED_W, y: (TOP_H - 14) >> 1, w: SPEED_W, h: 14 };
}

export function speedHit(uiW: number, x: number, y: number): boolean {
  return contains(speedRect(uiW), x, y);
}

function drawInspector(s: PixelSurface, state: HudState, layout: HudLayout): void {
  if (!state.inspector) return;
  const w = g(48);
  const paths = state.inspector.paths ?? [];
  // Exact: title block + subtitle + one row per line + upgrade rows + padding.
  // Panels are sized by their content, never by a guess with slack (doc 17 §8).
  const titleBlock = g(2) + font('header').capHeight + g(2) + 1 + g(2);
  const h =
    titleBlock +
    font('body').capHeight +
    g(2) +
    state.inspector.lines.length * g(4) +
    (paths.length > 0 ? g(1) + paths.length * g(5) : 0) +
    g(2);
  const r: Rect = { x: g(3), y: s.height - h - g(3), w, h };
  const content = drawTitledPanel(s, r, state.inspector.title);
  const body = font('body');
  const micro = font('micro');
  body.draw(s, state.inspector.subtitle, content.x, content.y, UI.inkDim);
  let y = content.y + body.capHeight + g(2);
  for (const line of state.inspector.lines) {
    const tone =
      line.tone === 'good'
        ? UI.good
        : line.tone === 'warning'
          ? UI.warning
          : line.tone === 'critical'
            ? UI.critical
            : UI.ink;
    drawStatRow(s, { x: content.x, y, w: content.w, h: g(4) }, line.label, line.value, tone);
    y += g(4);
  }
  // upgrade paths (doc 05): name · tier pips · next cost — the row is the button
  if (paths.length > 0) {
    y += g(1);
    for (const p of paths) {
      const row: Rect = { x: content.x, y, w: content.w, h: g(5) - 1 };
      layout.upgradeRows.push(row);
      if (p.buyable) {
        s.fillRect(row.x, row.y, row.w, row.h, UI.panelRimDark);
        s.hline(row.x, row.x + row.w - 1, row.y + row.h - 1, UI.panelRimLight);
      }
      // tier pips from glyphs the faces actually have: - empty · * bought · # capstone
      const pips = ['---', '*--', '**-', '**#'][p.tier] ?? '---';
      body.draw(s, `${p.key.toUpperCase()} ${p.name}`, row.x + g(1), row.y + g(1), p.tier > 0 ? UI.ink : UI.inkDim, { caps: true });
      const right = p.blocked ?? (p.nextCost !== null ? `+ ${p.nextCost}` : S.hud.max);
      const rightTone = p.blocked ? UI.inkDim : p.buyable ? UI.good : UI.inkDim;
      const rw = micro.measure(`${pips}  ${right}`, { caps: true });
      micro.draw(s, `${pips}  ${right}`, row.x + row.w - rw - g(1), row.y + g(1) + 2, rightTone, { caps: true });
      y += g(5);
    }
  }
}

function drawHotbar(s: PixelSurface, layout: HudLayout, state: HudState): void {
  layout.hotbar.forEach((r, i) => {
    const item = state.hotbar[i]!;
    const st: WidgetState = !item.affordable
      ? 'disabled'
      : i === state.hotbarSelected
        ? 'selected'
        : i === state.hotbarHover
          ? 'hover'
          : 'default';
    // with a thumbnail the button carries no text: the name lives in the
    // tooltip above it, which is where the eye goes anyway (doc 17 §8)
    drawButton(s, r, item.icon ? '' : item.label, st, { hotkey: item.hotkey, face: 'micro' });
    if (item.icon) {
      const nudge = st === 'hover' ? -1 : 0;
      s.blit(item.icon, r.x + ((r.w - item.icon.width) >> 1), r.y + ((r.h - item.icon.height) >> 1) + nudge);
      if (st === 'disabled') {
        for (let y = r.y + 1; y < r.y + r.h - 1; y++) {
          for (let x = r.x + 1; x < r.x + r.w - 1; x++) if (((x + y) & 1) === 0) s.px(x, y, UI.disabled);
        }
      }
    }
    // a locked slot reads as "not yours yet", never as "you are broke"
    if (item.locked) s.hline(r.x + 3, r.x + r.w - 4, r.y + (r.h >> 1), UI.critical);
  });
  const hovered = state.hotbar[state.hotbarHover];
  if (hovered) {
    drawTooltip(s, hovered.tooltip, layout.hotbar[state.hotbarHover]!, {
      w: s.width,
      h: s.height,
    });
  }
}

function drawToast(s: PixelSurface, state: HudState): void {
  if (!state.toast) return;
  const body = font('body');
  const w = body.measure(state.toast.text, { caps: true }) + g(4);
  // doc 17 §6: in from the right over 140 ms, out over 120. Integer px, never
  // sub-pixel — a slide that lands between pixels is what "web app" looks like.
  const x = s.width - w - g(3) + state.toast.slide;
  const r: Rect = { x, y: TOP_H + g(2), w, h: body.capHeight + g(4) };
  drawPanel(s, r, { raised: true });
  body.draw(s, state.toast.text, r.x + g(2), r.y + g(2), UI.warning, { caps: true });
}

function drawBanner(s: PixelSurface, state: HudState): void {
  const b = state.banner;
  if (!b || b.alpha <= 0) return;
  const display = font('display');
  const body = font('body');
  const w = Math.max(display.measure(b.title, { caps: true }), body.measure(b.sub, { caps: true })) + g(8);
  const h = display.capHeight + body.capHeight + g(8);
  const layer = new PixelSurface(w + 4, h + 4);
  const r: Rect = { x: 0, y: 0, w, h };
  drawPanel(layer, r, { raised: true });
  drawCentered(layer, display, b.title, w >> 1, g(3), UI.ink, { caps: true });
  drawCentered(layer, body, b.sub, w >> 1, g(3) + display.capHeight + g(2), UI.warning, { caps: true });
  if (b.alpha < 1) multiplyAlpha(layer, b.alpha);
  s.blit(layer, (s.width - w) >> 1, Math.round(s.height * 0.24));
}
